import { defineStore } from 'pinia'
import { EnumerationsRole } from 'client'
import { EnumerationsFirstRoute, EnumerationsLogoutType } from 'client/identity'
import { AxiosError } from 'axios'
import { useThemeStore } from './theme'
import type {
  AuthenticationLogoutResponse,
  ClientClientResponseDTO,
  CommandsAdminChangePasswordCommand,
  CommandsChangeExpiredPasswordRequest,
  CommandsChangePasswordCommand,
  CommandsLoginResponseDTO,
  CommandsMFALoginResponse,
  QueriesGetLoggedInUserDataResponse,
  QueriesGetSubdomainForUserResponse,
  QueriesUserServiceDTO,
} from 'client/identity'
import type { HeadersDefaults } from 'axios'
import type { Router } from 'vue-router'
import type { IFirstRouteOptions } from '~/helpers/login'
import { joinGroup, start } from '~/composables/signalR/useSignalR'
import { useNavigationStore } from '~/stores/navigation'
import { MESSAGE, PATH, STORE } from '~/constants/strings'
import { asyncBlobToBase64 } from '~/utils/transforms'
import { notifyFailure, notifySuccess } from '~/utils/notifications'
import {
  HNRV2,
  fileNetworkRequest,
  isBerkshireEmailAddress,
  redirectToNewSubdomain,
} from '~/helpers'
import { getSubdomain } from '~/helpers/api'
import { getPathForFirstRoute } from '~/helpers/login'
import { getViewAsUserQueryValue } from '~/api/instance'
import { api } from '~/services'
import { useUsersApiService } from '~/services/usersApiService'
import { useUserClaims } from '~/composables/useUserClaims'
import { getStoreKey } from '~/helpers/store'

export interface IViewAsHeaders extends HeadersDefaults {
  'X-View-As'?: string
}

export enum AuthStatus {
  Unauthenticated,
  Authenticated,
  InitiatingLogin,
  InitiatingLoginMfa,
  LoginError,
  LogoutError,
  PasswordExpired,
  GettingUser,
  UserLoaded,
  InitiatingUserSession,
  LoggedIn,
  InitiatingLoggingOut,
  UserLoggedOut,
  SystemLoggedOut,
  NearTimeOut,
}

interface AuthStoreState {
  email: string
  loading: boolean
  user: QueriesGetLoggedInUserDataResponse | null
  avatar: string | undefined
  justLoggedIn: boolean
  defaultClientSubdomain: string | undefined
  changePasswordToken: string
  authStatus: AuthStatus
  firstPath: string

  isNearTimeout: boolean
  validSubdomains: string[]

  qrCode: string | undefined
  failedMFALoginCount: number
  worker: Worker | null
  isViewAsUser: boolean
}

export const defaultAuthStoreState: AuthStoreState = {
  email: '',
  loading: false,
  user: null,
  avatar: '',
  justLoggedIn: false,
  defaultClientSubdomain: undefined,
  changePasswordToken: '',
  authStatus: AuthStatus.Unauthenticated,
  isNearTimeout: false,
  firstPath: '',
  validSubdomains: [],
  failedMFALoginCount: 0,
  qrCode: '',
  worker: null,
  isViewAsUser: false,
}

const userApi = useUsersApiService()

export const useAuthStore = defineStore(STORE.AUTH, {
  state: (): AuthStoreState => ({ ...defaultAuthStoreState }),
  getters: {
    isLoggedIn(): boolean {
      const validStatusesToShowLoggedIn = [
        AuthStatus.LoggedIn,
        AuthStatus.NearTimeOut,
      ]
      return validStatusesToShowLoggedIn.includes(this.authStatus)
    },
    showGlobalAppLoadingState(): boolean {
      const validStatusesToShowLoading = [
        AuthStatus.Authenticated,
        AuthStatus.GettingUser,
        AuthStatus.UserLoaded,
        AuthStatus.InitiatingUserSession,
        AuthStatus.InitiatingLoggingOut,
      ]

      return validStatusesToShowLoading.includes(this.authStatus)
    },
    hasAccessToMultipleClients(): boolean {
      return !!this.user?.userClientCount && this.user.userClientCount > 1
    },
    hasServiceLines(): boolean {
      return (
        !!this.user &&
        Array.isArray(this.user.services) &&
        this.user.services.length > 0
      )
    },
    isBerkshireRole(): boolean {
      const berkshireRoles: EnumerationsRole[] = [
        EnumerationsRole.BerkshireCSLeadership,
        EnumerationsRole.BerkshireDataAdmin,
        EnumerationsRole.BerkshireConsultant,
        EnumerationsRole.BerkshirePSAdmin,
        EnumerationsRole.BerkshireProductSupport,
        EnumerationsRole.BerkshireSystemAdmin,
      ]
      return !!this.user?.roleID && berkshireRoles.includes(this.user.roleID)
    },
    isBerkshireCSAdmin(): boolean {
      return this.user?.roleID === EnumerationsRole.BerkshireDataAdmin
    },
    isServicesClient(): boolean {
      const currentClient: ClientClientResponseDTO | undefined =
        this.user?.accessedClients?.find(
          (accessedClient) => accessedClient.clientID === this.user?.clientID
        )
      return currentClient?.servicesClient ?? false
    },
    hasBerkshireEmail(): boolean {
      return isBerkshireEmailAddress(this.user?.emailAddress)
    },
    firstAndLastName(): string {
      if (!this.user) return ''
      return `${this.user.firstName} ${this.user.lastName}`
    },
    hideSummaries(): boolean {
      const marriotClientID = '4776093B-17E4-41B7-8DE4-E5CC406CD6BC'
      const restrictedClientIDs = [
        marriotClientID,
        marriotClientID.toLowerCase(),
      ]

      return (
        !!this.user?.clientID &&
        restrictedClientIDs.includes(this.user.clientID) &&
        this.user?.roleID === EnumerationsRole.ReportViewer
      )
    },
    formattedValidSubdomains(): string[] {
      return (
        this.validSubdomains.map((subdomain) => subdomain.toLowerCase()) ?? []
      )
    },
    targetSubdomain(): string {
      // determine current subdomain
      // don't move if you are on a valid subdomain for this user
      const currentSubdomain: string = getSubdomain().toLowerCase()
      const userOnValidSubdomain: boolean =
        !!currentSubdomain &&
        [
          this.defaultClientSubdomain,
          ...this.formattedValidSubdomains,
        ].includes(currentSubdomain)
      const targetSubdomain: string = userOnValidSubdomain
        ? currentSubdomain
        : this.defaultClientSubdomain ?? ''
      return targetSubdomain
    },
  },
  actions: {
    async getLoggedInUserData(): Promise<QueriesGetLoggedInUserDataResponse | null> {
      const response = await HNRV2<QueriesGetLoggedInUserDataResponse>({
        fn: () => api.auth.apiIdentityAuthMeGet(),
        shouldErrorRedirect: false,
      })
      if (response && !(response instanceof AxiosError)) {
        return response
      }

      return null
    },
    async initializeUserTheme() {
      const ThemeStore = useThemeStore()
      try {
        if (this.user?.accessibilityMode) {
          await ThemeStore.setAccessibilityMode()
        } else {
          await ThemeStore.setDefaultMode()
        }
      } catch (error) {
        console.error('Error initializing user theme:', error)
      }
    },
    async checkValidCookie(): Promise<QueriesGetSubdomainForUserResponse | null> {
      const response = await HNRV2({
        fn: () => api.userContext.apiIdentityUserContextCheckValidCookieGet(),
        shouldErrorRedirect: false,
      })

      if (response instanceof AxiosError) {
        return null
      }

      return response
    },
    async setAvatar(newAvatar: Blob): Promise<boolean> {
      const response = await userApi.setAvatar(newAvatar)

      if (response instanceof AxiosError) {
        return false
      }

      return response.success
    },
    async getAvatar(): Promise<File | undefined> {
      const response = await fileNetworkRequest({
        fn: () => api.usersFile.apiIdentityUsersAvatarGet(),
      })

      if (response instanceof AxiosError) {
        return
      }
      return response.data
    },
    async getClientName(
      clientID: QueriesGetLoggedInUserDataResponse['clientID']
    ): Promise<string> {
      const getClientDataResponse = await HNRV2({
        fn: () =>
          api.clientManagement.apiIdentityClientManagementClientIDGet(clientID),
      })

      if (getClientDataResponse instanceof AxiosError) {
        return ''
      }

      return getClientDataResponse.clientData.clientName
    },
    async mfaLogin(
      code: string,
      fromSSO: boolean
    ): Promise<CommandsMFALoginResponse | AxiosError<unknown, any>> {
      const response = await HNRV2<CommandsMFALoginResponse>({
        fn: () =>
          api.auth.apiIdentityAuthMfaCodeFromSsoFromSSOPost(code, fromSSO),
        shouldErrorRedirect: false,
      })

      if (response instanceof AxiosError) {
        if (response.response?.status === 401) {
          this.failedMFALoginCount = this.failedMFALoginCount + 1
        }
        return response
      }

      if (response?.success) {
        this.failedMFALoginCount = 0
        this.qrCode = undefined
        this.validSubdomains = response.validSubdomains
        this.defaultClientSubdomain = response.defaultClientSubdomain
        return response
      }

      throw {
        message: 'Problem completing mfa login',
        response,
      }
    },
    async login(
      email: string,
      password: string,
      redirectID?: string
    ): Promise<{ success: boolean }> {
      this.authStatus = AuthStatus.InitiatingLogin

      const loginResponse = await HNRV2<CommandsLoginResponseDTO>({
        fn: () =>
          api.auth.apiIdentityAuthLoginPost({
            username: email,
            password,
            ...(redirectID && { redirectID }),
          }),
      })

      if (loginResponse instanceof AxiosError) {
        this.authStatus = AuthStatus.LoginError
        return { success: false }
      }

      const defaultClientSubdomain = loginResponse.defaultClientSubdomain
      const isPasswordExpired = loginResponse.passwordExpired
      const isMFALogin = loginResponse.redirectToMFA

      this.email = email
      this.defaultClientSubdomain = defaultClientSubdomain
      this.changePasswordToken = loginResponse.expiredPasswordToken

      if (isPasswordExpired) {
        this.authStatus = AuthStatus.PasswordExpired
        this.justLoggedIn = true
        await this.$router.push(PATH.PASSWORD_EXPIRED)
        return { success: false }
      }

      if (isMFALogin) {
        this.authStatus = AuthStatus.InitiatingLoginMfa
        this.justLoggedIn = false

        await (this.$router as Router).push({
          path: PATH.LOGIN_MFA,
        })
        return { success: false }
      }

      if (loginResponse.success) {
        this.authStatus = AuthStatus.Authenticated
        this.justLoggedIn = true
        const redirectURLFromLogin = loginResponse.redirectURL
        this.validSubdomains = loginResponse.validSubdomains

        // Redirect if necessary and then stop further execution
        await this.redirectToNewSubdomainIfNecessary({
          targetSubdomain: this.targetSubdomain,
          newPath: redirectURLFromLogin,
        })

        // no redirection necessary, return true
        return { success: true }
      }

      return { success: false }
    },
    async redirectToNewSubdomainIfNecessary({
      targetSubdomain,
      newPath,
      setOriginalPath = true,
    }: {
      targetSubdomain: string
      newPath?: string
      setOriginalPath?: boolean
    }): Promise<void> {
      const currentPath = window.location.pathname
      const currentParams = window.location.search
      const fullPath = currentPath + currentParams

      let originalPath: string | undefined = undefined

      if (setOriginalPath) {
        const pathAllowedAsOriginalPath = ![
          PATH.LOGIN,
          PATH.LOGIN_MFA,
          PATH.ROOT,
        ].includes(currentPath)

        if (pathAllowedAsOriginalPath) {
          originalPath = fullPath
        }

        // Parse the search query string
        // if someone is redirected from non subdomain
        const searchParams = new URLSearchParams(currentParams)
        const redirectUrl = searchParams.get('redirect') ?? newPath

        if (redirectUrl) {
          originalPath = decodeURIComponent(redirectUrl)
        }
      }

      const currentSubdomain = getSubdomain()
      if (currentSubdomain !== targetSubdomain) {
        // Going to the new subdomain will do a full-page refresh
        await redirectToNewSubdomain({
          newSubdomain: targetSubdomain,
          newPath: newPath ?? this.firstPath,
          originalPath,
        })
      }
    },
    async getFirstRouteEnum(): Promise<EnumerationsFirstRoute> {
      const response = await HNRV2({
        fn: () => api.routing.apiIdentityRoutingFirstGet(),
      })
      if (response instanceof AxiosError) {
        return EnumerationsFirstRoute.DocumentCenter
      }
      return response
    },
    async getFirstPath(
      userData: QueriesGetLoggedInUserDataResponse,
      firstPathEnum?: EnumerationsFirstRoute
    ): Promise<string> {
      const NavigationStore = useNavigationStore()

      const firstRouteEnumeration: EnumerationsFirstRoute | null =
        firstPathEnum || (await this.getFirstRouteEnum())

      if (firstRouteEnumeration === null) {
        return PATH.DOCUMENT_CENTER
      }

      const serviceLine: QueriesUserServiceDTO | undefined =
        (NavigationStore.findServiceLine() ||
          (userData.services[0] as QueriesUserServiceDTO | undefined)) ??
        undefined

      // Only call fetchFirstServiceDeliverableID if necessary
      let serviceDeliverableID = ''
      if (
        firstRouteEnumeration === EnumerationsFirstRoute.ServiceDeliverables &&
        serviceLine?.serviceID
      ) {
        serviceDeliverableID = serviceLine?.serviceID
          ? await this.fetchFirstServiceDeliverableID(serviceLine.serviceID)
          : ''
      }

      // Construct the route options
      const { shouldSeeSupportAccess } = useUserClaims()
      const routeOptions: IFirstRouteOptions = {
        route: firstRouteEnumeration,
        serviceLine: serviceLine?.name.toLowerCase() ?? undefined,
        serviceDeliverableID,
        role: userData.roleID,
        shouldSeeSupportAccess: shouldSeeSupportAccess.value,
        supportAccessPath: NavigationStore?.supportAccessPath ?? '',
      }
      // Get the first path using the existing function
      return getPathForFirstRoute(routeOptions)
    },

    async fetchFirstServiceDeliverableID(serviceID: string): Promise<string> {
      const NavigationStore = useNavigationStore()
      const result = await NavigationStore.getLeftNavMenu(serviceID)

      if (!result) {
        return ''
      }

      return result.serviceDeliverablePages[0]?.id ?? ''
    },
    // gets user data for user profile after edit
    async fetchUserData(): Promise<void> {
      const userData: QueriesGetLoggedInUserDataResponse | null =
        await this.getLoggedInUserData()

      if (userData) {
        this.user = userData
        this.email = this.user.emailAddress
      }
    },
    async fetchNavigationData(firstPath: string): Promise<void> {
      const NavigationStore = useNavigationStore()
      await NavigationStore.initializeNavigation(firstPath)
    },
    openNavItem(path: string, serviceName: string) {
      const NavigationStore = useNavigationStore()

      if (!serviceName) {
        NavigationStore.navItemOpen = ''
        return
      }

      const pathMapping: { [key: string]: string } = {
        'data-viz': 'DATA VISUALIZATIONS',
        reports: 'REPORTS',
        'service-deliverables': 'SERVICE DELIVERABLES',
      }

      // Split the path and filter out any empty strings
      const segments = path.split('/').filter((segment) => segment.length > 0)

      // Find the relevant segment for mapping
      const relevantSegment = segments.find(
        (segment, index) =>
          (segment !== serviceName && pathMapping[segment]) ||
          (segment === serviceName &&
            index < segments.length - 1 &&
            pathMapping[segments[index + 1]])
      )

      // Map the segment or return an empty string if no match is found
      const navItemOpen =
        relevantSegment === serviceName
          ? pathMapping[segments[segments.indexOf(relevantSegment) + 1]]
          : relevantSegment
          ? pathMapping[relevantSegment]
          : ''
      NavigationStore.navItemOpen = navItemOpen
    },
    // TODO: Move timeout session stuff to a composable(?) or other appropriate filetype
    //sets up worker that keeps track of when to check if logged in
    initializeSessionTimeoutWorker(): void {
      if (this.worker) {
        console.warn('Session timeout worker is already running.')
        return
      }

      if (window.Worker) {
        this.worker = new Worker('/timeoutWorker.js')
        this.worker.onmessage = async (e: MessageEvent) => {
          if (e.data.type === 'requestSessionCheck') {
            await this.timeoutWarning()
          }
        }
      }
    },
    resumeSessionTimeoutWorker() {
      if (this.worker) {
        this.worker.postMessage({ type: 'resume' })
      }
    },
    pauseSessionTimeoutWorker() {
      if (this.worker) {
        this.worker.postMessage({ type: 'pause' })
      }
    },

    terminateSessionTimeoutWorker() {
      if (this.worker) {
        this.worker.terminate()
        this.worker = null
      }
    },
    async convertAvatarBlob(avatar: Blob | null): Promise<string | undefined> {
      if (avatar && avatar?.size > 0) {
        const base64Avatar = await asyncBlobToBase64(avatar)
        return base64Avatar as string
      }
      return undefined
    },
    async fetchAvatarData(): Promise<void> {
      const avatar = await this.getAvatar()
      if (avatar instanceof AxiosError || !avatar) {
        this.avatar = ''
        return
      }
      this.avatar = await this.convertAvatarBlob(avatar)
    },
    async initializeUserIfLoggedIn(): Promise<void> {
      try {
        const { isLoggedIn, user, avatar, firstRouteEnum } =
          await this.getUserInformation()
        if (isLoggedIn && user?.emailAddress) {
          await this.initializeUser(user, avatar, firstRouteEnum)
        }
      } catch (error) {
        console.error('Error auto-checking login status:', error)
        this.authStatus = AuthStatus.LoginError
      }
    },
    async initializeUser(
      user: QueriesGetLoggedInUserDataResponse,
      avatar: Blob | null,
      firstRouteEnum: EnumerationsFirstRoute
    ): Promise<void> {
      this.user = user
      this.email = user.emailAddress
      this.authStatus = AuthStatus.UserLoaded
      this.avatar = await this.convertAvatarBlob(avatar)

      const calculatedFirstPath = await this.getFirstPath(user, firstRouteEnum)

      const redirectUrl = (this.$router as Router).currentRoute.value?.query
        ?.redirect as string | undefined

      this.firstPath =
        typeof redirectUrl === 'string' && redirectUrl
          ? decodeURIComponent(redirectUrl)
          : calculatedFirstPath

      await this.initializeUserSession(this.firstPath)
    },
    // gets user data for login / check auth / everything other than user profile (see fetchUserData)
    async getUserInformation(): Promise<{
      isLoggedIn: boolean
      user: QueriesGetLoggedInUserDataResponse | null
      avatar: Blob | null
      firstRouteEnum: EnumerationsFirstRoute
    }> {
      const [user, avatar, firstRouteEnum] = await Promise.all([
        this.getLoggedInUserData(),
        this.getAvatar(),
        this.getFirstRouteEnum(),
      ])
      const isLoggedIn = user?.emailAddress ? true : false
      return {
        isLoggedIn,
        user: user ?? null,
        avatar: avatar ?? null,
        firstRouteEnum: firstRouteEnum ?? '',
      }
    },
    async getFirstValidPath(
      firstPath: string,
      viewAsUser: boolean
    ): Promise<string> {
      // Create a new URL object to extract the pathname
      const url = new URL(firstPath, window.location.origin)
      const fromWelcomePage = url.pathname === '/welcome'

      // Check if it's the first path, if we are viewing as a user, or if it's from the welcome page
      if (!firstPath || viewAsUser || fromWelcomePage) {
        const [user, firstRouteEnum] = await Promise.all([
          this.getLoggedInUserData(),
          this.getFirstRouteEnum(),
        ])
        return await this.getFirstPath(user, firstRouteEnum)
      }

      // Return the pathname without query parameters
      return url.pathname
    },
    async initializeUserSession(path?: string): Promise<void> {
      const viewAsUser = getViewAsUserQueryValue()
      // if path is empty because its not set then use first path value
      try {
        const signalRInit = async () => {
          await start()
          await joinGroup()
        }

        // if path is empty because its not set then use first path value
        const initialUserPath = path || this.firstPath
        this.loading = true
        this.authStatus = AuthStatus.InitiatingUserSession

        await signalRInit()

        const pathWithoutQuery = await this.getFirstValidPath(
          initialUserPath,
          Boolean(viewAsUser)
        )

        const url = new URL(initialUserPath, window.location.origin)
        const existingQueryParams = Object.fromEntries(
          url.searchParams.entries()
        )

        const isWelcomePage = url.pathname === '/welcome'

        const query = {
          ...(isWelcomePage ? {} : existingQueryParams),
          ...(viewAsUser && { viewAsUser }),
        }

        await Promise.all([
          this.fetchNavigationData(pathWithoutQuery),
          this.initializeUserTheme(),
          this.$router.push({
            path: pathWithoutQuery,
            query,
          }),
        ])

        const NavigationStore = useNavigationStore()

        this.initializeSessionTimeoutWorker()

        const serviceLineFromPath =
          NavigationStore.findServiceLine(pathWithoutQuery)
        this.openNavItem(initialUserPath, serviceLineFromPath?.name ?? '')

        this.authStatus = AuthStatus.LoggedIn
      } catch (error) {
        console.error('Error initializing user session:', error)
        this.authStatus = AuthStatus.LoginError
      } finally {
        this.loading = false
      }
    },
    async logout(type: EnumerationsLogoutType): Promise<void> {
      const cleanupViewAs = ({
        departingPath,
      }: {
        departingPath: string
      }): string => {
        let updatedDepartingPath = departingPath
        if (currentRoute.query.viewAsUser) {
          updatedDepartingPath = updatedDepartingPath
            .replace(/([&?])viewAsUser=[\da-f-]+&?/, '$1')
            .replace(/\?$/, '')
        }
        // remove X-View-As from headers
        delete (instance.defaults.headers as IViewAsHeaders)['X-View-As']
        this.isViewAsUser = false

        return updatedDepartingPath
      }
      const handleSuccessfulLogoutRedirect = ({
        responseData,
        logoutType,
        departingPath,
      }: {
        responseData: AuthenticationLogoutResponse
        logoutType: EnumerationsLogoutType
        departingPath: string
      }) => {
        if (!responseData?.redirectUrl) {
          console.error('Logout response data is undefined')
          this.authStatus = AuthStatus.LogoutError
          return
        }

        const path = responseData.redirectUrl.endsWith('/')
          ? responseData.redirectUrl.slice(0, -1)
          : responseData.redirectUrl

        let query = ''
        if (
          !responseData.isSSOLogout &&
          logoutType !== EnumerationsLogoutType.Manual
        ) {
          const hasExistingParams = path.includes('?')
          query = `${departingPath}`

          if (logoutType === EnumerationsLogoutType.Timeout) {
            query += hasExistingParams ? '&timeout=true' : '?timeout=true'
          }
        }

        window.location.assign(`${path}${query}`)
      }
      const handleResetPersistentProcesses = () => {
        this.terminateSessionTimeoutWorker()
      }

      this.authStatus = AuthStatus.InitiatingLoggingOut
      const currentRoute = this.$router.currentRoute.value
      const departingPath = currentRoute.fullPath
      const updatedDepartingPath = cleanupViewAs({ departingPath })

      const logoutResponse = await HNRV2({
        fn: () => api.auth.apiIdentityAuthLogoutPost(type),
        shouldErrorRedirect: false,
      })

      if (logoutResponse instanceof AxiosError) {
        console.error('Unable to logout successfully', logoutResponse)

        if (logoutResponse.response?.status === 401) {
          handleResetPersistentProcesses()
          window.location.assign(PATH.LOGIN)
        } else {
          this.authStatus = AuthStatus.LogoutError
        }
        return
      }

      handleResetPersistentProcesses()

      handleSuccessfulLogoutRedirect({
        responseData: logoutResponse,
        logoutType: type,
        departingPath: updatedDepartingPath,
      })
    },
    async updateUserPassword(
      userID: string | undefined,
      newPassword: string,
      confirmPassword: string
    ): Promise<boolean> {
      const passwordsMatch = newPassword === confirmPassword

      if (!passwordsMatch) {
        notifyFailure('Passwords do not match')
        return false
      }

      const payload: CommandsAdminChangePasswordCommand = {
        userID,
        newPassword,
        confirmPassword,
      }

      const updateUserPasswordResponse = await HNRV2({
        fn: () => api.auth.apiIdentityAuthAdminChangePasswordPut(payload),
      })

      if (updateUserPasswordResponse instanceof AxiosError) {
        notifyFailure(updateUserPasswordResponse.message.replace(/["']/g, ''))
        return false
      }

      notifySuccess(MESSAGE.USER_PASSWORD_UPDATE_SUCCESS)
      return true
    },
    async changePassword({
      currentPassword,
      newPassword,
      passwordConfirmation,
    }: {
      currentPassword: string
      newPassword: string
      passwordConfirmation: string
    }): Promise<boolean> {
      const payload: CommandsChangePasswordCommand = {
        username: this.email,
        oldPassword: currentPassword,
        newPassword,
        confirmPassword: passwordConfirmation,
      }

      const changePasswordResponse = await HNRV2({
        fn: () => api.auth.apiIdentityAuthChangePasswordPut(payload),
      })

      if (!changePasswordResponse) {
        return false
      }

      if (changePasswordResponse instanceof AxiosError) {
        if (changePasswordResponse.response?.status === 400) {
          notifyFailure(
            'Value entered for current password does not match. Please try again.'
          )
          return false
        }

        notifyFailure(changePasswordResponse.message)
        return false
      }

      if (changePasswordResponse.success) {
        notifySuccess('Successfully changed password')
        return true
      }

      return false
    },
    async changeExpiredPassword(
      payload: CommandsChangeExpiredPasswordRequest
    ): Promise<boolean> {
      const response = await HNRV2({
        fn: () => api.auth.apiIdentityAuthExpiredPasswordPut(payload),
      })

      if (response instanceof AxiosError || response.success === false) {
        notifyFailure(response.message)
        return false
      }

      if (response?.success) {
        notifySuccess('Successfully changed Password')
        return true
      }

      notifyFailure('Failed to change password')
      return false
    },
    async timeoutWarning(): Promise<void> {
      const isNearTimeout = await HNRV2<boolean>({
        fn: () => api.auth.apiIdentityAuthTimeoutWarningGet(),
        shouldErrorRedirect: false,
      })

      if (
        isNearTimeout instanceof AxiosError &&
        isNearTimeout.response?.status === 401
      ) {
        // Terminate the session timeout worker and log out
        this.terminateSessionTimeoutWorker()
        const NavigationStore = useNavigationStore()
        this.$reset()
        NavigationStore.$reset()
        this.authStatus = AuthStatus.SystemLoggedOut
        if ((this.$router as Router).currentRoute.value.meta.requiresAuth) {
          await this.logout(EnumerationsLogoutType.Timeout)
        }

        return
      }

      if (isNearTimeout) {
        this.isNearTimeout = true
      } else {
        this.isNearTimeout = false
      }
    },

    async getRemainingSessionTime(): Promise<string> {
      const remainingSessionTime = await HNRV2<string>({
        fn: () => api.auth.apiIdentityAuthTimeBeforeExpirationGet(),
      })

      if (!remainingSessionTime || remainingSessionTime instanceof AxiosError) {
        return ''
      }

      return remainingSessionTime
    },
    async getQrCode() {
      const qrCodeResult = await HNRV2<string>({
        fn: () => api.auth.apiIdentityAuthQrGet(),
      })

      if (typeof qrCodeResult === 'string') {
        this.qrCode = qrCodeResult
      }
    },
  },
  persist: { pick: ['qrCode', 'avatar'], key: getStoreKey(STORE.AUTH) },
})
